import axios from "axios";
import { API_ADDRESS } from "..";
import { GetToken } from "../helper";

interface Material {
  id: number;
  stockCode: string;
  materialName: string;
  materialSecondName?: string;
  price: number;
  currency: string;
  createdDate: string;
  updatedDate?: string;
  createdBy?: string;
  updatedBy?: string;
}

interface MaterialCreateModel {
  stockCode: string;
  materialName: string;
  materialSecondName?: string;
  price: number;
  currency: string;
}

interface MaterialUpdateModel {
  id: number;
  stockCode: string;
  materialName: string;
  materialSecondName?: string;
  price: number;
  currency: string;
}

interface MaterialSearchModel {
  searchTerm?: string;
  page: number;
  pageSize: number;
}

interface MaterialResponseModel {
  success: boolean;
  message: string;
  data?: any;
  errors?: string[];
}

interface MaterialListResponse {
  materials: Material[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface MaterialCSVImportModel {
  stockCode: string;
  materialName: string;
  materialSecondName?: string;
  price: number;
  currency: string;
}

class MaterialService {
  static async getMaterials(
    searchModel: MaterialSearchModel
  ): Promise<MaterialResponseModel> {
    try {
      const params = new URLSearchParams();
      if (searchModel.searchTerm) {
        params.append("searchTerm", searchModel.searchTerm);
      }
      params.append("page", searchModel.page.toString());
      params.append("pageSize", searchModel.pageSize.toString());

      const response = await axios.get(
        `${API_ADDRESS}/api/Material/GetMaterials?${params.toString()}`,
        {
          headers: {
            Authorization: "Bearer " + GetToken(),
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.error("Error getting materials:", error);
      return {
        success: false,
        message:
          error.response?.data?.message ||
          "Malzemeler getirilirken hata oluştu",
      };
    }
  }

  static async getMaterial(id: number): Promise<MaterialResponseModel> {
    try {
      const response = await axios.get(
        `${API_ADDRESS}/api/Material/GetMaterial/${id}`,
        {
          headers: {
            Authorization: "Bearer " + GetToken(),
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.error("Error getting material:", error);
      return {
        success: false,
        message:
          error.response?.data?.message || "Malzeme getirilirken hata oluştu",
      };
    }
  }

  static async createMaterial(
    material: MaterialCreateModel
  ): Promise<MaterialResponseModel> {
    try {
      const response = await axios.post(
        `${API_ADDRESS}/api/Material/CreateMaterial`,
        material,
        {
          headers: {
            Authorization: "Bearer " + GetToken(),
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.error("Error creating material:", error);
      return {
        success: false,
        message:
          error.response?.data?.message || "Malzeme oluşturulurken hata oluştu",
      };
    }
  }

  static async updateMaterial(
    material: MaterialUpdateModel
  ): Promise<MaterialResponseModel> {
    try {
      const response = await axios.put(
        `${API_ADDRESS}/api/Material/UpdateMaterial`,
        material,
        {
          headers: {
            Authorization: "Bearer " + GetToken(),
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.error("Error updating material:", error);
      return {
        success: false,
        message:
          error.response?.data?.message || "Malzeme güncellenirken hata oluştu",
      };
    }
  }

  static async deleteMaterial(id: number): Promise<MaterialResponseModel> {
    try {
      const response = await axios.delete(
        `${API_ADDRESS}/api/Material/DeleteMaterial/${id}`,
        {
          headers: {
            Authorization: "Bearer " + GetToken(),
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.error("Error deleting material:", error);
      return {
        success: false,
        message:
          error.response?.data?.message || "Malzeme silinirken hata oluştu",
      };
    }
  }

  static async importCSV(
    materials: MaterialCSVImportModel[]
  ): Promise<MaterialResponseModel> {
    try {
      const response = await axios.post(
        `${API_ADDRESS}/api/Material/ImportCSV`,
        materials,
        {
          headers: {
            Authorization: "Bearer " + GetToken(),
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.error("Error importing CSV:", error);
      return {
        success: false,
        message:
          error.response?.data?.message || "CSV içe aktarılırken hata oluştu",
      };
    }
  }

  static async bulkDeleteMaterials(
    materialIds: number[]
  ): Promise<MaterialResponseModel> {
    try {
      const response = await axios.post(
        `${API_ADDRESS}/api/Material/BulkDelete`,
        materialIds,
        {
          headers: {
            Authorization: "Bearer " + GetToken(),
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.error("Error bulk deleting materials:", error);
      return {
        success: false,
        message:
          error.response?.data?.message ||
          "Toplu silme işlemi sırasında hata oluştu",
      };
    }
  }
}

export { MaterialService };
export type {
  Material,
  MaterialCreateModel,
  MaterialCSVImportModel,
  MaterialListResponse,
  MaterialResponseModel,
  MaterialSearchModel,
  MaterialUpdateModel,
};
